import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Divider,
  Checkbox,
  Tooltip,
} from '@chakra-ui/react'
import { createShippingLineSchema, updateShippingLineSchema, type CreateShippingLineFormData, type UpdateShippingLineFormData } from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

interface ShippingLineFormFormProps {
  shippingLineId?: number
  onSuccess: () => void
  onCancel: () => void
}

export function ShippingLineForm({ shippingLineId, onSuccess, onCancel }: ShippingLineFormFormProps) {
  const colors = useThemeColors()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!shippingLineId
  const { showError, showSuccess } = useToastNotification()

  type SelectedItem = {
    parent_id: number
    name: string
    }
  const [selectedShippingDataOption, setSelectedShippingDataOption] = useState<number[]>([]);
  const [selectedTransactionDataOption, setSelectedTransactionDataOption] = useState<number[]>([]);
  const [shippingLineDataOption, setShippingLineDataOption] = useState<{ id: number; parent_id: number; name: string }[]>([])
  const [transactionDataOption, setTransactionDataOption] = useState<{ id: number; parent_id: number; name: string }[]>([])

  

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateShippingLineFormData | UpdateShippingLineFormData>({
    resolver: zodResolver(isEditMode ? updateShippingLineSchema : createShippingLineSchema),
    defaultValues: {
      name: '',
      email_address: '',
      address: '',
      contact_name: '',
      contact_mobile: '',
      fax_no: '',
      tin: '',
      landlines: [],
      shipping_lines_template: [],
      transaction_information_template: [],
    },
  })

  useEffect(() => {
  const fetchData = async () => {
    try {
      // Always fetch Shipping Line Template Data Options
      const shippingLineDataResponse = await apiClient.get(`options/soa-data-options/parents/1/children`);
      const shippingLineData = shippingLineDataResponse.data?.data || shippingLineDataResponse.data || [];
      setShippingLineDataOption(shippingLineData);

      // Fetch Transaction Line Template Data Options
      const transactionDataResponse = await apiClient.get(`options/soa-data-options/parents/2/children`);
      const transactionData = transactionDataResponse.data?.data || transactionDataResponse.data || [];
      setTransactionDataOption(transactionData);

      // If edit mode, fetch shipping line details
      if (isEditMode && shippingLineId) {
        const response = await apiClient.get(`/shipping-lines/${shippingLineId}`);
        const shippingLine = response.data?.data || response.data || {};

        reset({
          name: shippingLine.name || '',
          email_address: shippingLine.email_address || '',
          address: shippingLine.address || '',
          contact_name: shippingLine.contact_name || '',
          contact_mobile: shippingLine.contact_mobile || '',
          fax_no: shippingLine.fax_no || '',
          tin: shippingLine.tin || '',
          landlines: Array.isArray(shippingLine.landlines)
            ? shippingLine.landlines.filter(Boolean)
            : typeof shippingLine.landlines === 'string'
            ? [shippingLine.landlines]  // convert single string to array
            : [],
          shipping_lines_template: shippingLine.shipping_lines_template ?? [],
          transaction_information_template: shippingLine.transaction_information_template ?? [],
        });

        // Insert Database Values into their Respective Checkboxes
        setSelectedShippingDataOption(shippingLine.shipping_lines_template ?? []);
        setSelectedTransactionDataOption(shippingLine.transaction_information_template ?? []);
      }
      else{
        // use the variables you JUST fetched
        setSelectedShippingDataOption(shippingLineData.map((item: { id: number }) => item.id));
        setSelectedTransactionDataOption(transactionData.map((item: { id: number }) => item.id));

      }
    } catch (error: any) {
      showError(error);
    }
  };
  fetchData();
}, [isEditMode, shippingLineId, reset, showError]);
  

  const onSubmit = async (data: CreateShippingLineFormData | UpdateShippingLineFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: data.name,
        email_address: data.email_address,
        address: data.address || '',
        contact_name: data.contact_name || '',
        contact_mobile: data.contact_mobile || '',
        fax_no: data.fax_no || '',
        tin: data.tin || '',
        // Accept either array or comma-separated string from the form and normalize to string[]
        landlines:
          Array.isArray((data as any).landlines) ? (data as any).landlines : typeof (data as any).landlines === 'string' ? 
          (data as any).landlines.split(',').map((s: string) => s.trim()).filter(Boolean) : [],  
        shipping_lines_template: selectedShippingDataOption, // <-- only IDs
        transaction_information_template: selectedTransactionDataOption,  // <-- only IDs
      }

      if (isEditMode) {
        await apiClient.put(`/shipping-lines/${shippingLineId}`, payload)
        showSuccess('Success', 'Shipping Line updated successfully')
      } else {
        await apiClient.post('/shipping-lines', payload)
        showSuccess('Success', 'Shipping Line created successfully')
      }

      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Shipping Line Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. John Doe"/>
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Email Address <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.email_address}>
            <Input {...register('email_address')} placeholder="e.g. john.doe@gmail.com"/>
            <FormErrorMessage>{errors.email_address?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Address <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.address}>
            <Input {...register('address')} placeholder="e.g. 123 Rizal Ave, Brgy. San Lorenzo, Makati City 1223"/>
            <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Contact Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.contact_name}>
            <Input {...register('contact_name')} placeholder="e.g. John Doe,"/>
            <FormErrorMessage>{errors.contact_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Contact Mobile <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.contact_mobile}>
            <Input {...register('contact_mobile')} placeholder="e.g. +63-917-123-4567"/>
            <FormErrorMessage>{errors.contact_mobile?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Landline
          </FormLabel>
          <FormControl isInvalid={!!errors.landlines}>
            <Input {...register('landlines')} placeholder="e.g. +63-2-123-4567, +63-2-345-6789 (Seperate Landline Numbers using a Comma)"/>
            <FormErrorMessage>{errors.landlines?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Fax No
          </FormLabel>
          <FormControl isInvalid={!!errors.fax_no}>
            <Input {...register('fax_no')} placeholder="e.g. +63-2-987-6543"/>
            <FormErrorMessage>{errors.fax_no?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Tin <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.tin}>
            <Input {...register('tin')} placeholder="e.g. 123-456-789-000"/>
            <FormErrorMessage>{errors.tin?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Divider/>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
            Statement of Account Template <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            {shippingLineDataOption.map(item => (
            <Box key={item.name} display="flex" alignItems="center">
                <Checkbox
                    isChecked={selectedShippingDataOption.some(i => i === item.id)}
                    onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                        setSelectedShippingDataOption(prev => [...prev, item.id]);
                    } else {
                        setSelectedShippingDataOption(prev => prev.filter(i => i !== item.id));
                    }
                    }}
                >
                    <Text fontSize="sm">{item.name}</Text>
                </Checkbox>
            </Box>
            ))}
            </Grid>
        </Grid>
      
        <Divider/>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
            Transaction Template <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            {transactionDataOption.map(item => (
            <Box key={item.name} display="flex" alignItems="center">
                <Checkbox
                    isChecked={selectedTransactionDataOption.some(i => i === item.id)}
                    onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                        setSelectedTransactionDataOption(prev => [...prev, item.id]);
                    } else {
                        setSelectedTransactionDataOption(prev => prev.filter(i => i !== item.id));
                    }
                    }}
                >
                    <Text fontSize="sm">{item.name}</Text>
                </Checkbox>
            </Box>
            ))}
            </Grid>
        </Grid>

        <Divider/>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Shipping Line'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}